import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FadeIn } from './FadeIn';
import { Button } from './Button';
import { FAQ } from './FAQ';
import { LegalModal, PrivacyPolicyContent, TermsOfServiceContent } from './LegalModal';
import { Shield, XCircle, CheckCircle2, FileText, PhoneCall, Calculator, FileWarning, ArrowRight, HeartHandshake } from 'lucide-react';
export function LandingPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-[#FDF8F0] selection:bg-[#7B2D3B]/20" data-id="element-12">
      {/* Navigation / Top Bar */}
      <div className="w-full bg-white/50 backdrop-blur-sm border-b border-[#7B2D3B]/10 py-4 sticky top-0 z-50" data-id="element-13">
        <div className="max-w-3xl mx-auto px-6 flex justify-between items-center" data-id="element-14">
          <span className="font-serif font-semibold text-[#7B2D3B] text-lg tracking-tight" data-id="element-15">
            El Plan de Rescate
          </span>
          <button onClick={scrollToPricing} className="text-sm font-medium text-[#7B2D3B] hover:text-[#5a212b] transition-colors" data-id="element-16">
            Obtener Acceso
          </button>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-6 py-16 md:py-24 space-y-24 md:space-y-32" data-id="element-17">
        {/* HERO SECTION */}
        <section className="text-center space-y-8" data-id="element-18">
          <FadeIn data-id="element-19">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7B2D3B]/10 text-[#7B2D3B] text-sm font-medium mb-6" data-id="element-20">
              <Shield className="w-4 h-4" data-id="element-21" />
              <span data-id="element-22">Para la Cónyuge Sobreviviente</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-id="element-23">
              El Plan de Rescate para la Cónyuge Sobreviviente
            </h1>
            <h2 className="text-xl md:text-2xl text-gray-600 font-serif leading-relaxed max-w-2xl mx-auto" data-id="element-24">
              Asegura el máximo cheque mensual de viudez que te corresponde
              legalmente y recupera tu paz financiera tras la pérdida de tu
              esposo.
            </h2>
          </FadeIn>
          <FadeIn delay={0.15} data-id="element-25">
            <div className="flex justify-center" data-id="element-26">
              <img src="https://cdn.magicpatterns.com/uploads/vxL5u5aajWiABkjymUnqMQ/Abuelita_2.png" alt="El Plan de Rescate — Cónyuge Sobreviviente, mostrado en una tablet" className="w-full max-w-md rounded-2xl shadow-xl" data-id="element-27" />
            </div>
          </FadeIn>
          <FadeIn delay={0.3} data-id="element-28">
            <div className="pt-4" data-id="element-29">
              <Button onClick={scrollToPricing} className="mx-auto" data-id="element-30">
                Obtener El Plan de Rescate Ahora{' '}
                <ArrowRight className="w-5 h-5 ml-1" data-id="element-31" />
              </Button>
            </div>
          </FadeIn>
        </section>

        {/* OPENING HOOK */}
        <section data-id="element-32">
          <FadeIn data-id="element-33">
            <div className="bg-white/60 p-8 md:p-10 rounded-2xl shadow-sm border border-[#7B2D3B]/10 relative" data-id="element-34">
              <HeartHandshake className="w-10 h-10 text-[#C9A84C] absolute -top-5 -left-5 bg-[#FDF8F0] rounded-full p-2" data-id="element-35" />
              <p className="text-lg md:text-xl leading-relaxed italic text-gray-700" data-id="element-36">
                "Estás en la mesa de la cocina, rodeada de papeles y facturas,
                con un nudo en el estómago que no desaparece. La pérdida de tu
                esposo ya es suficiente dolor; no deberías tener que cargar
                también con el miedo a perder tu casa o no poder cubrir tus
                gastos básicos. Sabes que el Seguro Social tiene un beneficio
                para ti, pero el miedo a cometer un error en el trámite o a que
                te den menos dinero del que te pertenece te mantiene despierta a
                las 2 de la mañana."
              </p>
            </div>
          </FadeIn>
        </section>

        {/* THE PROBLEM */}
        <section className="space-y-8" data-id="element-37">
          <FadeIn data-id="element-38">
            <h3 className="text-3xl font-bold text-center mb-8" data-id="element-39">
              La dura realidad del sistema
            </h3>
            <p className="text-lg leading-relaxed text-gray-700 mb-6" data-id="element-40">
              En los Estados Unidos, el sistema del Seguro Social no está
              diseñado para ayudarte de forma proactiva. Ellos no te dirán cuál
              es la mejor estrategia para tu caso específico a menos que tú
              sepas exactamente qué preguntar.
            </p>

            <div className="bg-[#7B2D3B] text-white p-8 rounded-2xl shadow-lg my-10" data-id="element-41">
              <p className="text-xl md:text-2xl font-serif leading-relaxed text-center" data-id="element-42">
                Muchas viudas latinas pierden entre{' '}
                <strong className="text-[#C9A84C] text-3xl" data-id="element-43">
                  $300 y $600 dólares mensuales
                </strong>{' '}
                —dinero que ya pagaron con años de trabajo y esfuerzo—
                simplemente porque llamaron a la oficina del gobierno sin
                preparación, aceptaron la primera opción que les ofrecieron o
                esperaron demasiado para reclamar, perdiendo meses de beneficios
                que no son retroactivos.
              </p>
            </div>
          </FadeIn>
        </section>

        {/* WHY WHAT YOU TRIED DIDN'T WORK */}
        <section className="space-y-8" data-id="element-44">
          <FadeIn data-id="element-45">
            <h3 className="text-3xl font-bold text-center mb-10" data-id="element-46">
              Por qué lo que has intentado no funciona
            </h3>
            <div className="space-y-4" data-id="element-47">
              {['Confiar en consejos de amigas o familiares que usaron leyes que ya caducaron.', 'Intentar navegar el sitio web de la SSA en inglés, sintiendo que un error de traducción podría arruinarlo todo.', 'Llamar a la línea general y recibir respuestas cortas de agentes que solo quieren colgar el teléfono.', 'Esperar a que el gobierno te contacte automáticamente tras reportar el fallecimiento (esto casi nunca sucede para los beneficios de sobreviviente).'].map((item, i) => <div key={i} className="flex items-start gap-4 bg-white/40 p-5 rounded-xl" data-id="element-48">
                  <XCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" data-id="element-49" />
                  <p className="text-gray-700 text-lg" data-id="element-50">{item}</p>
                </div>)}
            </div>
          </FadeIn>
        </section>

        {/* THE UNIQUE MECHANISM */}
        <section data-id="element-51">
          <FadeIn data-id="element-52">
            <div className="border-2 border-[#C9A84C] bg-white rounded-3xl p-8 md:p-12 text-center shadow-sm relative overflow-hidden" data-id="element-53">
              <div className="absolute top-0 left-0 w-full h-2 bg-[#C9A84C]" data-id="element-54"></div>
              <h3 className="text-2xl md:text-3xl font-bold mb-6" data-id="element-55">
                La Solución: El "Filtro de Doble Beneficio"
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-56">
                He creado un sistema de decisión lógica que te permite comparar
                tu historial de trabajo contra el de tu esposo y determinar el
                momento exacto (mes y año) para activar tu reclamo. Esta
                estrategia de{' '}
                <strong className="text-[#7B2D3B]" data-id="element-57">"Cambio de Carril"</strong>{' '}
                permite que tu propio cheque crezca mientras cobras el de
                viudez, garantizando que nunca dejes dinero sobre la mesa por el
                resto de tu vida.
              </p>
            </div>
          </FadeIn>
        </section>

        {/* WHAT YOU GET */}
        <section className="space-y-10" data-id="element-58">
          <FadeIn data-id="element-59">
            <h3 className="text-3xl font-bold text-center mb-10" data-id="element-60">
              Lo que incluye El Plan de Rescate
            </h3>
            <div className="grid gap-6" data-id="element-61">
              {[{
              icon: FileText,
              title: 'La Guía Maestra: El Plan de Rescate (Kit de Rescate Financiero)',
              desc: 'Con lenguaje claro y sin tecnicismos.'
            }, {
              icon: PhoneCall,
              title: 'El Guion de Llamadas',
              desc: 'Qué decir palabra por palabra para que el agente de la SSA te respete y te dé la información correcta.'
            }, {
              icon: Shield,
              title: "Lista de Verificación 'Carpeta de Acero'",
              desc: 'Los 7 documentos originales que debes tener antes de tu cita.'
            }, {
              icon: Calculator,
              title: 'Calculadora de Decisión',
              desc: 'Una hoja de trabajo sencilla para saber cuál cheque es mayor.'
            }, {
              icon: FileWarning,
              title: 'Guía de Impuestos y Multas',
              desc: 'Cómo evitar que el IRS se quede con tu dinero si decides seguir trabajando.'
            }].map((item, i) => <motion.div key={i} className="flex items-start gap-5 bg-white p-6 rounded-2xl shadow-md border border-gray-100 cursor-default" animate={{
              y: [0, -6, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.4
            }} whileHover={{
              scale: 1.03,
              boxShadow: '0 10px 40px rgba(123,45,59,0.12)'
            }} data-id="element-62">
                  <div className="bg-[#FDF8F0] p-3 rounded-full text-[#7B2D3B]" data-id="element-63">
                    <item.icon className="w-6 h-6" data-id="element-64" />
                  </div>
                  <div data-id="element-65">
                    <h4 className="font-bold text-xl mb-1" data-id="element-66">{item.title}</h4>
                    <p className="text-gray-600" data-id="element-67">{item.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </FadeIn>
          <FadeIn delay={0.15} data-id="element-68">
            <div className="flex justify-center pt-4" data-id="element-69">
              <Button onClick={scrollToPricing} data-id="element-70">
                Sí, quiero mi Plan de Rescate
              </Button>
            </div>
          </FadeIn>
        </section>

        {/* WHO THIS IS FOR / NOT FOR */}
        <section className="grid md:grid-cols-2 gap-8" data-id="element-71">
          <FadeIn data-id="element-72">
            <div className="bg-white rounded-2xl p-8 shadow-sm h-full border-t-4 border-green-500" data-id="element-73">
              <h4 className="font-bold text-2xl mb-6 flex items-center gap-2" data-id="element-74">
                <CheckCircle2 className="w-6 h-6 text-green-500" data-id="element-75" /> Esto ES para
                ti si:
              </h4>
              <ul className="space-y-4" data-id="element-76">
                {['Viudas residentes o ciudadanas en USA de 60 años o más.', 'Mujeres que estuvieron casadas al menos 9 meses antes del fallecimiento.', 'Divorciadas (que estuvieron casadas 10 años) cuyo ex-esposo ha fallecido.', 'Quienes sienten que la información oficial es demasiado confusa o fría.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-77">
                    <span className="text-green-500 mt-1" data-id="element-78">•</span>
                    <span data-id="element-79">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-80">
            <div className="bg-white rounded-2xl p-8 shadow-sm h-full border-t-4 border-red-400" data-id="element-81">
              <h4 className="font-bold text-2xl mb-6 flex items-center gap-2" data-id="element-82">
                <XCircle className="w-6 h-6 text-red-400" data-id="element-83" /> Esto NO es para ti
                si:
              </h4>
              <ul className="space-y-4" data-id="element-84">
                {['Personas que nunca estuvieron casadas legalmente.', 'Matrimonios de menos de 9 meses (a menos que haya sido una muerte accidental).', 'Quienes buscan asesoría legal para casos de fraude o situaciones criminales.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-85">
                    <span className="text-red-400 mt-1" data-id="element-86">•</span>
                    <span data-id="element-87">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </section>

        {/* HOW IT WORKS */}
        <section className="space-y-10" data-id="element-88">
          <FadeIn data-id="element-89">
            <h3 className="text-3xl font-bold text-center mb-12" data-id="element-90">
              Cómo funciona
            </h3>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-[#7B2D3B]/20 before:to-transparent" data-id="element-91">
              {[{
              step: '1',
              title: 'Descarga el Plan',
              desc: 'Obtén acceso inmediato a la guía y las listas de verificación.'
            }, {
              step: '2',
              title: 'Prepara tu Carpeta',
              desc: 'Usa el Kit Maestro para organizar tus documentos en una tarde.'
            }, {
              step: '3',
              title: 'Haz la Llamada',
              desc: 'Usa el guion estratégico para agendar tu cita y reclamar lo que es tuyo con total confianza.'
            }].map((item, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-92">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-[#FDF8F0] bg-[#7B2D3B] text-white font-bold text-xl shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-md z-10" data-id="element-93">
                    {item.step}
                  </div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-94">
                    <h4 className="font-bold text-xl mb-2 text-[#7B2D3B]" data-id="element-95">
                      {item.title}
                    </h4>
                    <p className="text-gray-600" data-id="element-96">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </section>

        {/* PROOF / BELIEVABILITY */}
        <section data-id="element-97">
          <FadeIn data-id="element-98">
            <div className="bg-[#7B2D3B]/5 rounded-3xl p-8 md:p-12 text-center border border-[#7B2D3B]/10" data-id="element-99">
              <h3 className="text-2xl font-bold mb-4" data-id="element-100">
                Lo que cambia en 7 días:
              </h3>
              <p className="text-xl text-gray-800 leading-relaxed font-serif" data-id="element-101">
                Pasarás de la parálisis por análisis y el miedo a la pobreza, a
                tener una fecha de cita confirmada, tu documentación organizada
                y la certeza matemática de que estás recibiendo hasta el último
                centavo que el sistema permite.
              </p>
            </div>
          </FadeIn>
        </section>

        {/* FAQ */}
        <section className="space-y-8" data-id="element-102">
          <FadeIn data-id="element-103">
            <h3 className="text-3xl font-bold text-center mb-10" data-id="element-104">
              Preguntas Frecuentes
            </h3>
            <FAQ data-id="element-105" />
          </FadeIn>
        </section>

        {/* OFFER + PRICE BLOCK */}
        <section id="pricing" className="scroll-mt-24" data-id="element-106">
          <FadeIn data-id="element-107">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100" data-id="element-108">
              <div className="bg-[#7B2D3B] p-8 md:p-10 text-center text-white" data-id="element-109">
                <h3 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-110">
                  Protege tu futuro hoy mismo
                </h3>
                <p className="text-lg text-white/90 max-w-xl mx-auto" data-id="element-111">
                  No permitas que la burocracia se quede con el patrimonio que
                  tú y tu esposo construyeron. Hazlo por una fracción de lo que
                  representa un solo cheque mensual.
                </p>
              </div>
              <div className="p-8 md:p-12 text-center space-y-8 bg-gradient-to-b from-white to-[#FDF8F0]/50" data-id="element-112">
                <div data-id="element-113">
                  <div className="inline-block bg-red-500 text-white text-sm font-bold px-4 py-1.5 rounded-full mb-4" data-id="element-114">
                    🔥 AHORRA 73% — Oferta por tiempo limitado
                  </div>
                  <p className="text-gray-500 font-medium uppercase tracking-wider mb-2" data-id="element-115">
                    Precio Especial
                  </p>
                  <div className="flex items-center justify-center gap-3" data-id="element-116">
                    <span className="text-2xl text-gray-400 line-through font-medium" data-id="element-117">
                      $27
                    </span>
                    <span className="text-6xl font-bold text-gray-900" data-id="element-118">
                      $7.36
                    </span>
                    <span className="text-xl text-gray-500 font-medium mt-4" data-id="element-119">
                      USD
                    </span>
                  </div>
                  <p className="text-[#7B2D3B] font-medium mt-4 flex items-center justify-center gap-2" data-id="element-120">
                    <CheckCircle2 className="w-5 h-5" data-id="element-121" /> Acceso Permanente y
                    Descarga Inmediata
                  </p>
                </div>

                <div className="max-w-md mx-auto" data-id="element-122">
                  <Button href="https://www.earlysmileco.com/cart/53680713466222:1" className="w-full py-5 text-xl shadow-xl" data-id="element-123">
                    Haz clic aquí para obtener El Plan de Rescate ahora
                  </Button>
                  <p className="text-sm text-gray-500 mt-4" data-id="element-124">
                    Pago 100% seguro. Acceso inmediato al Kit de Rescate
                    Financiero.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* FINAL CTA */}
        <section className="text-center pb-10" data-id="element-125">
          <FadeIn data-id="element-126">
            <h2 className="text-2xl md:text-3xl font-serif text-[#7B2D3B] mb-8" data-id="element-127">
              Tu tranquilidad financiera está a un solo clic de distancia.
            </h2>
          </FadeIn>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="bg-white border-t border-gray-200 py-8 text-center text-gray-500 text-sm" data-id="element-128">
        <div className="max-w-3xl mx-auto px-6" data-id="element-129">
          <div className="flex items-center justify-center gap-4 mb-4" data-id="element-130">
            <button onClick={() => setPrivacyOpen(true)} className="text-[#7B2D3B] hover:text-[#5a212b] underline underline-offset-2 transition-colors" data-id="element-131">
              Política de Privacidad
            </button>
            <span className="text-gray-300" data-id="element-132">|</span>
            <button onClick={() => setTermsOpen(true)} className="text-[#7B2D3B] hover:text-[#5a212b] underline underline-offset-2 transition-colors" data-id="element-133">
              Términos de Servicio
            </button>
          </div>
          <p data-id="element-134">
            © {new Date().getFullYear()} El Plan de Rescate. Todos los derechos
            reservados.
          </p>
          <p className="mt-2 text-xs text-gray-400 max-w-xl mx-auto" data-id="element-135">
            Este sitio no está afiliado ni respaldado por la Administración del
            Seguro Social (SSA) ni por ninguna agencia gubernamental. La
            información proporcionada es para fines educativos.
          </p>
        </div>
      </footer>

      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-136">
        <PrivacyPolicyContent data-id="element-137" />
      </LegalModal>
      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos de Servicio" data-id="element-138">
        <TermsOfServiceContent data-id="element-139" />
      </LegalModal>
    </div>;
}